import React, { useState, useEffect, useMemo } from 'react';
import { MyWebRequest, getWebRequestPhaseInfo } from '../types';
import { motion, AnimatePresence } from 'motion/react';
import confetti from 'canvas-confetti';
import {
  Heart,
  Share2,
  Copy,
  Check,
  QrCode,
  Sparkles,
  Calendar,
  Clock,
  Flower2,
  Mail,
  ArrowLeft,
  MessageCircle,
  ExternalLink,
  Volume2,
  VolumeX,
  Send,
  Gift,
  BookOpen,
  Camera,
  Star,
  Quote,
  ShieldCheck,
  Lock,
  Trash2,
} from 'lucide-react';
import { romanticAudio } from '../utils/audio';

interface PersonalizedCoupleWebsiteProps {
  request: MyWebRequest;
  onBackToApp?: () => void;
  isAdmin?: boolean;
  onDeleteRequest?: (id: string) => Promise<void>;
}

interface FlowerItem {
  id: string;
  type: 'rose' | 'tulip' | 'sunflower' | 'daisy';
  color: string;
  name: string;
  plantedAt: Date;
  message: string;
}

export const PersonalizedCoupleWebsite: React.FC<PersonalizedCoupleWebsiteProps> = ({
  request,
  onBackToApp,
  isAdmin = false,
  onDeleteRequest,
}) => {
  const [copiedLink, setCopiedLink] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeLetter, setActiveLetter] = useState<{ title: string; content: string; date: string } | null>(null);
  const [isMuted, setIsMuted] = useState(false);

  // Handle administrator deleting this web directly
  const handleConfirmDelete = async () => {
    if (!onDeleteRequest) return;
    setIsDeleting(true);
    try {
      await onDeleteRequest(request.id);
      setShowDeleteModal(false);
      romanticAudio.playChime();
      if (onBackToApp) {
        onBackToApp();
      }
    } catch (err) {
      console.error('Error al eliminar la web:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  // Time elapsed state
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const timeData = useMemo(() => {
    const start = request.anniversaryDate
      ? new Date(request.anniversaryDate)
      : new Date(request.createdAt);

    const diffMs = Math.max(0, now.getTime() - start.getTime());
    const totalSeconds = Math.floor(diffMs / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const years = Math.floor(days / 365);
    const months = Math.floor((days % 365) / 30);
    const remainingDays = (days % 365) % 30;

    return { days, hours, minutes, seconds, years, months, remainingDays };
  }, [now, request.anniversaryDate, request.createdAt]);

  // Virtual Garden state
  const [gardenFlowers, setGardenFlowers] = useState<FlowerItem[]>([
    {
      id: '1',
      type: 'tulip',
      color: '#e11d48',
      name: 'Primer Latido',
      plantedAt: new Date(),
      message: 'El día en que comenzó nuestra historia mágica.',
    },
    {
      id: '3',
      type: 'sunflower',
      color: '#f59e0b',
      name: 'Luz de mi Vida',
      plantedAt: new Date(),
      message: 'Eres la alegría y sol que ilumina todos mis días.',
    },
  ]);

  const [flowerMessage, setFlowerMessage] = useState('');
  const [selectedFlowerType, setSelectedFlowerType] = useState<'rose' | 'tulip' | 'sunflower'>('rose');

  // URL of this personal website
  const shareUrl = useMemo(() => {
    if (typeof window !== 'undefined') {
      const base = window.location.origin + window.location.pathname;
      return `${base}?web=${encodeURIComponent(request.orderCode)}`;
    }
    return `https://nuestrosrecuerdos.app/?web=${request.orderCode}`;
  }, [request.orderCode]);

  // QR Code URL
  const qrCodeImageUrl = useMemo(() => {
    return `https://api.qrserver.com/v1/create-qr-code/?size=320x320&data=${encodeURIComponent(shareUrl)}&margin=10`;
  }, [shareUrl]);

  const handleCopyLink = () => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(shareUrl);
      setCopiedLink(true);
      romanticAudio.playBloomSuccess();
      setTimeout(() => setCopiedLink(false), 3000);
    }
  };

  const handleShareWhatsApp = () => {
    const text = encodeURIComponent(
      `Mi amor ${request.partnerName}, preparé este sitio web interactivo solo para nosotros dos ❤️✨:\n\n${shareUrl}`
    );
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const handlePlantFlower = (e: React.FormEvent) => {
    e.preventDefault();
    romanticAudio.playBloomSuccess();

    // Heart particles
    try {
      confetti({
        particleCount: 35,
        spread: 60,
        origin: { y: 0.7 },
        colors: ['#f43f5e', '#fb7185', '#fda4af', '#f59e0b', '#ec4899'],
      });
    } catch {}

    const newFlower: FlowerItem = {
      id: Date.now().toString(),
      type: selectedFlowerType,
      color: selectedFlowerType === 'rose' ? '#e11d48' : selectedFlowerType === 'tulip' ? '#ec4899' : '#f59e0b',
      name: selectedFlowerType === 'rose' ? 'Rosa Eterna' : selectedFlowerType === 'tulip' ? 'Tulipán del Alma' : 'Girasol Radiante',
      plantedAt: new Date(),
      message: flowerMessage.trim() || `Un detalle especial de ${request.applicantName} para ${request.partnerName}.`,
    };

    setGardenFlowers((prev) => [newFlower, ...prev]);
    setFlowerMessage('');
  };

  // Letters tailored for this couple
  const coupleLetters = useMemo(() => {
    return [
      {
        id: 'letter-1',
        title: `Para mi persona favorita: ${request.partnerName}`,
        date: request.anniversaryDate || 'Hoy y siempre',
        preview: 'Si pudiera volver a elegirte mil veces, lo haría sin pensarlo ni un segundo...',
        content: `Mi amada ${request.partnerName},\n\nCreé este rincón interactivo para recordarte lo inmensamente feliz que me haces cada día. No importa cuánto tiempo pase, cada instante a tu lado es mi lugar favorito en el mundo.\n\nGracias por tu ternura, tus risas, tu paciencia y por ser mi compañera de vida. Este espacio es nuestro, para celebrar nuestro amor hoy, mañana y siempre.\n\nCon todo mi amor,\n${request.applicantName} ❤️`,
      },
      {
        id: 'letter-2',
        title: 'Nuestra promesa de amor',
        date: 'Para la eternidad',
        preview: 'Prometo cuidarte, escucharte, hacerte reír y nunca soltar tu mano...',
        content: `Para ${request.partnerName},\n\nPrometo celebrar contigo cada triunfo, apoyarte en cada día difícil y recordarte siempre lo valiosa e increíble que eres para mí.\n\nNuestro amor no se mide en días, sino en momentos inolvidables. Te amo con todo el corazón.\n\n— ${request.applicantName}`,
      },
      {
        id: 'letter-3',
        title: 'Razones por las que te amo',
        date: 'Cada instante',
        preview: 'Tu sonrisa que ilumina cualquier día gris, la paz que siento al abrazarte...',
        content: `1. Por la forma en que me miras y me haces sentir en casa.\n2. Por tu risa contagiosa que alegra mis días.\n3. Por ser mi mayor cómplice, mi amor y mi mejor amiga.\n4. Porque a tu lado, todo tiene más sentido y más color.\n\nSiempre tuyo,\n${request.applicantName}`,
      },
    ];
  }, [request.applicantName, request.partnerName, request.anniversaryDate]);

  const phaseInfo = getWebRequestPhaseInfo(request.status);

  if (!phaseInfo.isApproved) {
    return (
      <div className="min-h-screen bg-[#FFFDFB] text-[#302B30] flex flex-col items-center justify-center p-4 relative overflow-hidden font-sans">
        {/* Admin Bar for unapproved state */}
        {isAdmin && (
          <div className="absolute top-0 inset-x-0 bg-white/95 backdrop-blur-md text-[#302B30] px-4 py-2.5 border-b border-[#F0DDE4] z-50 text-xs shadow-xs">
            <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-300 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3 text-amber-500" />
                  <span>Administrador</span>
                </span>
                <span className="text-[#302B30] font-semibold">
                  Web en revisión (#{request.orderCode})
                </span>
              </div>
              <div className="flex items-center gap-2">
                {onBackToApp && (
                  <button
                    type="button"
                    onClick={onBackToApp}
                    className="px-3 py-1.5 rounded-xl bg-[#FFF4F7] hover:bg-[#FFE8EF] text-[#302B30] text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer border border-[#F0DDE4]"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    <span>Volver al panel</span>
                  </button>
                )}
                {onDeleteRequest && (
                  <button
                    type="button"
                    onClick={() => setShowDeleteModal(true)}
                    className="px-3 py-1.5 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>Eliminar web</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="max-w-md w-full rounded-3xl bg-white border border-[#F0DDE4] p-6 sm:p-8 text-center space-y-6 shadow-[0_20px_60px_rgba(232,93,142,0.12)] relative z-10 mt-12 sm:mt-0">
          <div className="w-16 h-16 rounded-2xl bg-amber-50 text-amber-600 border border-amber-200 flex items-center justify-center mx-auto">
            <Lock className="w-8 h-8 text-amber-500" />
          </div>

          <div className="space-y-2">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-bold uppercase tracking-wider">
              <Clock className="w-3.5 h-3.5 animate-spin text-amber-500" />
              <span>Fase {phaseInfo.phase} de 4: {phaseInfo.title}</span>
            </div>
            <h1 className="text-xl sm:text-2xl font-bold font-['Playfair_Display',serif] text-[#302B30]">
              Sitio Web en Proceso de Aprobación
            </h1>
            <p className="text-xs sm:text-sm text-[#77717A] leading-relaxed">
              La web interactiva para <strong className="text-[#302B30]">{request.applicantName}</strong> & <strong className="text-[#302B30]">{request.partnerName}</strong> (#{request.orderCode}) aún no ha sido aprobada por el administrador.
            </p>
          </div>

          {/* Stepper */}
          <div className="grid grid-cols-4 gap-1 py-3 border-y border-[#F0DDE4] text-center">
            <div className={`space-y-1 ${phaseInfo.phase >= 1 ? 'text-emerald-600' : 'text-stone-300'}`}>
              <div className="w-6 h-6 rounded-full bg-[#FFF4F7] border border-current flex items-center justify-center mx-auto text-[10px] font-bold">1</div>
              <div className="text-[10px] font-semibold">Enviado</div>
            </div>
            <div className={`space-y-1 ${phaseInfo.phase >= 2 ? 'text-blue-600' : 'text-stone-300'}`}>
              <div className="w-6 h-6 rounded-full bg-[#FFF4F7] border border-current flex items-center justify-center mx-auto text-[10px] font-bold">2</div>
              <div className="text-[10px] font-semibold">Recibido</div>
            </div>
            <div className={`space-y-1 ${phaseInfo.phase >= 3 ? 'text-amber-600' : 'text-stone-300'}`}>
              <div className="w-6 h-6 rounded-full bg-[#FFF4F7] border border-current flex items-center justify-center mx-auto text-[10px] font-bold">3</div>
              <div className="text-[10px] font-semibold">En revisión</div>
            </div>
            <div className={`space-y-1 ${phaseInfo.isApproved ? 'text-emerald-600' : 'text-stone-300'}`}>
              <div className="w-6 h-6 rounded-full bg-[#FFF4F7] border border-current flex items-center justify-center mx-auto text-[10px] font-bold">4</div>
              <div className="text-[10px] font-semibold">Aprobado</div>
            </div>
          </div>

          <div className="p-3.5 rounded-2xl bg-[#FFF4F7] border border-[#F0DDE4] text-xs text-[#77717A]">
            🔒 El enlace interactivo y todas las secciones de la web se habilitan automáticamente cuando el administrador finaliza la revisión y aprueba la solicitud (Fase 4).
          </div>

          {onBackToApp && (
            <button
              type="button"
              onClick={onBackToApp}
              className="w-full py-3 px-5 rounded-2xl bg-gradient-to-r from-[#E85D8E] to-[#FF8DAF] hover:from-[#DF4D80] hover:to-[#FF7A9F] text-white text-xs font-bold transition-all shadow-md shadow-[#E85D8E]/20 cursor-pointer flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Volver a la aplicación principal</span>
            </button>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/35 backdrop-blur-md">
            <div className="bg-white border border-[#F0DDE4] rounded-3xl p-6 max-w-md w-full text-[#302B30] space-y-4 shadow-2xl">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 bg-rose-50 text-rose-600 border border-rose-100">
                  <Trash2 className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-[#302B30]">
                    ¿Eliminar esta web personalizada?
                  </h3>
                  <p className="text-xs text-[#77717A] font-mono mt-0.5">
                    #{request.orderCode} — {request.applicantName} y {request.partnerName}
                  </p>
                </div>
              </div>
              <p className="text-xs text-[#77717A] leading-relaxed">
                Esta acción eliminará de forma irreversible toda la información de esta web de la base de datos de Firestore.
              </p>
              <div className="flex items-center justify-end gap-2.5 pt-2">
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 rounded-xl bg-[#FFF4F7] hover:bg-[#FFE8EF] text-[#302B30] border border-[#F0DDE4] text-xs font-bold transition-all cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={handleConfirmDelete}
                  className="px-5 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold transition-all cursor-pointer shadow-md"
                >
                  {isDeleting ? 'Eliminando...' : 'Sí, eliminar web'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-linear-to-b from-rose-50/70 via-stone-50 to-amber-50/40 text-stone-800 pb-24 selection:bg-rose-200 selection:text-rose-950 font-sans">
      {/* Top Banner Navigation for Custom Website */}
      {isAdmin && (
        <div className="bg-white/95 backdrop-blur-md text-[#302B30] px-4 py-2.5 border-b border-[#F0DDE4] sticky top-0 z-50 text-xs shadow-xs">
          <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 rounded-full bg-[#FFF4F7] text-[#E85D8E] border border-[#F0DDE4] text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
                <ShieldCheck className="w-3 h-3 text-[#E85D8E]" />
                <span>Administrador</span>
              </span>
              <span className="text-[#302B30] font-semibold">
                Web de {request.applicantName} y {request.partnerName}
              </span>
              <span className="text-[#77717A] font-mono text-[11px]">#{request.orderCode}</span>
              <span className="hidden sm:inline text-[#77717A]">• Fase {phaseInfo.phase}: {phaseInfo.title}</span>
            </div>

            <div className="flex items-center gap-2">
              {onBackToApp && (
                <button
                  type="button"
                  onClick={onBackToApp}
                  className="px-3 py-1.5 rounded-xl bg-[#FFF4F7] hover:bg-[#FFE8EF] text-[#302B30] text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer border border-[#F0DDE4]"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  <span>Volver al panel</span>
                </button>
              )}

              {onDeleteRequest && (
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(true)}
                  className="px-3 py-1.5 rounded-xl bg-rose-600/90 hover:bg-rose-600 text-white text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer shadow-sm"
                  title="Eliminar esta web permanentemente de Firestore"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Eliminar web</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <header className={`sticky ${isAdmin ? 'top-[45px]' : 'top-0'} z-40 bg-white/80 backdrop-blur-xl border-b border-rose-100/80 shadow-2xs`}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            {onBackToApp && (
              <button
                type="button"
                onClick={onBackToApp}
                className="p-2 rounded-xl text-stone-500 hover:text-stone-900 hover:bg-stone-100 transition-colors cursor-pointer"
                title="Volver a la aplicación principal"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <div>
              <div className="flex items-center gap-1.5 font-['Playfair_Display',serif] font-bold text-base sm:text-lg text-stone-900">
                <span>{request.applicantName}</span>
                <Heart className="w-4 h-4 text-rose-500 fill-rose-500 animate-pulse inline" />
                <span>{request.partnerName}</span>
              </div>
              <div className="flex items-center gap-1.5 text-[10px] sm:text-xs text-rose-600 font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block animate-ping" />
                <span>Sitio Web Exclusivo Interactivo</span>
                <span className="text-stone-300">•</span>
                <span className="text-stone-400 font-mono">{request.orderCode}</span>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCopyLink}
              className="py-1.5 px-3 rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-700 font-semibold text-xs flex items-center gap-1.5 transition-all cursor-pointer shadow-2xs"
              title="Copiar enlace directo a esta web"
            >
              {copiedLink ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5 text-stone-600" />}
              <span className="hidden sm:inline">{copiedLink ? '¡Enlace copiado!' : 'Copiar link'}</span>
            </button>

            <button
              type="button"
              onClick={handleShareWhatsApp}
              className="py-1.5 px-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs flex items-center gap-1.5 transition-all cursor-pointer shadow-2xs shadow-emerald-600/20"
              title="Compartir por WhatsApp"
            >
              <MessageCircle className="w-3.5 h-3.5 fill-current" />
              <span className="hidden sm:inline">WhatsApp</span>
            </button>

            <button
              type="button"
              onClick={() => setShowQrModal(true)}
              className="p-2 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-600 transition-colors cursor-pointer"
              title="Ver código QR para escanear"
            >
              <QrCode className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Container */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12 space-y-12">
        {/* HERO SECTION */}
        <section className="text-center space-y-6 relative">
          <div className="inline-flex items-center gap-2 py-1.5 px-4 rounded-full bg-rose-100/80 text-rose-700 text-xs font-semibold shadow-2xs">
            <Sparkles className="w-3.5 h-3.5 text-rose-500 fill-rose-500" />
            <span>Nuestra Historia de Amor • Espacio Privado</span>
          </div>

          <h1 className="font-['Playfair_Display',serif] text-4xl sm:text-6xl md:text-7xl font-bold text-stone-900 tracking-tight leading-[1.1]">
            <span className="block text-stone-900">{request.applicantName}</span>
            <span className="text-rose-500 font-serif italic text-3xl sm:text-5xl mx-2">&</span>
            <span className="block text-stone-900">{request.partnerName}</span>
          </h1>

          {/* Dedication Banner */}
          {request.dedication ? (
            <div className="max-w-2xl mx-auto p-6 sm:p-8 rounded-3xl bg-white/90 border border-rose-100 shadow-sm relative">
              <Quote className="w-8 h-8 text-rose-200 fill-rose-100 absolute -top-4 left-6" />
              <p className="font-['Caveat',cursive] text-2xl sm:text-3xl text-stone-800 leading-relaxed pt-2">
                "{request.dedication}"
              </p>
              <div className="pt-3 text-xs text-rose-500 font-semibold tracking-wider uppercase">
                Con todo el amor de {request.applicantName}
              </div>
            </div>
          ) : (
            <p className="font-['Caveat',cursive] text-2xl sm:text-3xl text-stone-700 max-w-xl mx-auto">
              "Cada momento a tu lado se convierte en mi recuerdo favorito."
            </p>
          )}
        </section>

        {/* LIVE TIME COUNTER SECTION */}
        <section className="bg-white/90 backdrop-blur-md rounded-3xl border border-rose-100/80 p-6 sm:p-10 shadow-sm text-center space-y-6">
          <div className="space-y-1">
            <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-rose-600">
              <Clock className="w-4 h-4" />
              <span>Tiempo de Nuestro Amor</span>
            </div>
            <h2 className="font-['Playfair_Display',serif] text-2xl sm:text-3xl font-bold text-stone-900">
              Cada segundo juntos vale una eternidad
            </h2>
            {request.anniversaryDate && (
              <p className="text-xs sm:text-sm text-stone-500">
                Desde nuestro aniversario:{' '}
                <strong className="text-stone-800 font-semibold">
                  {new Date(request.anniversaryDate).toLocaleDateString('es-ES', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </strong>
              </p>
            )}
          </div>

          {/* Counter Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 max-w-2xl mx-auto">
            <div className="p-4 rounded-2xl bg-rose-50/70 border border-rose-100/80">
              <div className="font-['Playfair_Display',serif] text-3xl sm:text-4xl font-extrabold text-rose-600">
                {timeData.days}
              </div>
              <div className="text-xs font-semibold text-stone-600 uppercase tracking-wider mt-1">Días</div>
            </div>

            <div className="p-4 rounded-2xl bg-rose-50/70 border border-rose-100/80">
              <div className="font-['Playfair_Display',serif] text-3xl sm:text-4xl font-extrabold text-stone-800">
                {String(timeData.hours).padStart(2, '0')}
              </div>
              <div className="text-xs font-semibold text-stone-600 uppercase tracking-wider mt-1">Horas</div>
            </div>

            <div className="p-4 rounded-2xl bg-rose-50/70 border border-rose-100/80">
              <div className="font-['Playfair_Display',serif] text-3xl sm:text-4xl font-extrabold text-stone-800">
                {String(timeData.minutes).padStart(2, '0')}
              </div>
              <div className="text-xs font-semibold text-stone-600 uppercase tracking-wider mt-1">Minutos</div>
            </div>

            <div className="p-4 rounded-2xl bg-rose-50/70 border border-rose-100/80">
              <div className="font-['Playfair_Display',serif] text-3xl sm:text-4xl font-extrabold text-rose-500 animate-pulse">
                {String(timeData.seconds).padStart(2, '0')}
              </div>
              <div className="text-xs font-semibold text-stone-600 uppercase tracking-wider mt-1">Segundos</div>
            </div>
          </div>
        </section>

        {/* INTERACTIVE COUPLE GARDEN */}
        <section className="bg-white/90 backdrop-blur-md rounded-3xl border border-rose-100/80 p-6 sm:p-10 shadow-sm space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-rose-100/80 pb-5">
            <div>
              <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-rose-600">
                <Flower2 className="w-4 h-4" />
                <span>Jardín Privado de la Pareja</span>
              </div>
              <h2 className="font-['Playfair_Display',serif] text-2xl sm:text-3xl font-bold text-stone-900">
                Flores cultivadas para ti
              </h2>
            </div>
            <div className="text-xs font-semibold text-rose-700 bg-rose-50 py-1.5 px-3.5 rounded-full self-start sm:self-auto border border-rose-200">
              {gardenFlowers.length} {gardenFlowers.length === 1 ? 'flor florecida' : 'flores florecidas'}
            </div>
          </div>

          {/* Plant Flower Form */}
          <form onSubmit={handlePlantFlower} className="p-4 rounded-2xl bg-stone-50/90 border border-stone-200/70 space-y-3">
            <div className="flex items-center gap-2 text-xs font-bold text-stone-800">
              <Sparkles className="w-3.5 h-3.5 text-rose-500" />
              <span>Plantar una nueva flor para {request.partnerName}</span>
            </div>

            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 text-xs text-stone-700 cursor-pointer">
                <input
                  type="radio"
                  name="flowerType"
                  value="rose"
                  checked={selectedFlowerType === 'rose'}
                  onChange={() => setSelectedFlowerType('rose')}
                  className="accent-rose-500"
                />
                <span>Rosa Roja</span>
              </label>

              <label className="flex items-center gap-1.5 text-xs text-stone-700 cursor-pointer">
                <input
                  type="radio"
                  name="flowerType"
                  value="tulip"
                  checked={selectedFlowerType === 'tulip'}
                  onChange={() => setSelectedFlowerType('tulip')}
                  className="accent-rose-500"
                />
                <span>Tulipán Rosado</span>
              </label>

              <label className="flex items-center gap-1.5 text-xs text-stone-700 cursor-pointer">
                <input
                  type="radio"
                  name="flowerType"
                  value="sunflower"
                  checked={selectedFlowerType === 'sunflower'}
                  onChange={() => setSelectedFlowerType('sunflower')}
                  className="accent-rose-500"
                />
                <span>Girasol</span>
              </label>
            </div>

            <div className="flex gap-2">
              <input
                type="text"
                value={flowerMessage}
                onChange={(e) => setFlowerMessage(e.target.value)}
                placeholder={`Dedicatoria para ${request.partnerName}... (ej. Gracias por existir)`}
                className="flex-1 py-2 px-3.5 rounded-xl bg-white border border-stone-200 text-xs sm:text-sm text-stone-800 placeholder-stone-400 focus:outline-hidden focus:ring-2 focus:ring-rose-500/20 focus:border-rose-400"
              />
              <button
                type="submit"
                className="py-2 px-4 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs sm:text-sm transition-all cursor-pointer flex items-center gap-1.5 shadow-2xs"
              >
                <Heart className="w-3.5 h-3.5 fill-current" />
                <span>Plantar</span>
              </button>
            </div>
          </form>

          {/* Flowers Display Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {gardenFlowers.map((flower) => (
              <motion.div
                key={flower.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="p-4 rounded-2xl bg-white border border-rose-100/90 shadow-2xs hover:shadow-md transition-shadow relative overflow-hidden group"
              >
                <div className="flex items-start gap-3">
                  <div
                    className="w-10 h-10 rounded-2xl flex items-center justify-center text-white shrink-0 shadow-2xs"
                    style={{ backgroundColor: flower.color }}
                  >
                    <Flower2 className="w-5 h-5 fill-current" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="font-bold text-xs sm:text-sm text-stone-900 group-hover:text-rose-600 transition-colors">
                      {flower.name}
                    </h3>
                    <p className="text-xs text-stone-600 leading-relaxed font-['Caveat',cursive] text-[15px]">
                      "{flower.message}"
                    </p>
                    <div className="text-[10px] text-stone-400 pt-1">
                      Cultivada con amor por {request.applicantName}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* LETTERS OF LOVE FOR THE COUPLE */}
        <section className="bg-white/90 backdrop-blur-md rounded-3xl border border-rose-100/80 p-6 sm:p-10 shadow-sm space-y-6">
          <div className="space-y-1">
            <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-rose-600">
              <Mail className="w-4 h-4" />
              <span>Cartas & Promesas</span>
            </div>
            <h2 className="font-['Playfair_Display',serif] text-2xl sm:text-3xl font-bold text-stone-900">
              Palabras que viajan directo al alma
            </h2>
            <p className="text-xs sm:text-sm text-stone-500">
              Toca cada carta para abrir el mensaje sellado con amor.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {coupleLetters.map((letter) => (
              <motion.div
                key={letter.id}
                whileHover={{ y: -4 }}
                onClick={() => {
                  setActiveLetter(letter);
                  romanticAudio.playChime();
                }}
                className="p-5 rounded-2xl bg-amber-50/60 border border-amber-200/60 shadow-2xs hover:shadow-md transition-all cursor-pointer space-y-3 relative group"
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-rose-600 bg-rose-50 py-1 px-2.5 rounded-full border border-rose-100">
                    {letter.date}
                  </span>
                  <div className="w-7 h-7 rounded-full bg-rose-600 text-white flex items-center justify-center shadow-2xs shadow-rose-600/30 group-hover:scale-110 transition-transform">
                    <Heart className="w-3.5 h-3.5 fill-current" />
                  </div>
                </div>

                <h3 className="font-['Playfair_Display',serif] font-bold text-sm sm:text-base text-stone-900 group-hover:text-rose-600 transition-colors">
                  {letter.title}
                </h3>

                <p className="text-xs text-stone-600 line-clamp-3 leading-relaxed">
                  {letter.preview}
                </p>

                <div className="pt-2 text-xs font-bold text-rose-600 flex items-center gap-1">
                  <span>Abrir carta</span>
                  <ExternalLink className="w-3.5 h-3.5" />
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ACTIVATED MODULES DOSSIER */}
        {request.desiredFeatures && request.desiredFeatures.length > 0 && (
          <section className="bg-white/80 rounded-3xl border border-stone-200/70 p-6 sm:p-8 space-y-4 shadow-2xs">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
              <h3 className="font-bold text-sm sm:text-base text-stone-900">
                Módulos y funciones habilitados para esta web:
              </h3>
            </div>

            <div className="flex flex-wrap gap-2">
              {request.desiredFeatures.map((feat) => (
                <div
                  key={feat}
                  className="py-1 px-3 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-xs font-semibold flex items-center gap-1.5"
                >
                  <Check className="w-3 h-3 text-emerald-600" />
                  <span>{feat}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* FOOTER OF PERSONAL WEBSITE */}
        <footer className="text-center pt-8 border-t border-rose-100/80 space-y-3">
          <div className="flex items-center justify-center gap-2 font-['Playfair_Display',serif] font-bold text-base text-stone-800">
            <span>{request.applicantName}</span>
            <Heart className="w-4 h-4 text-rose-500 fill-rose-500" />
            <span>{request.partnerName}</span>
          </div>
          <p className="text-xs text-stone-400">
            Página web personalizada interactiva creada con amor • Enlace privado único
          </p>
          <div className="pt-2 flex justify-center gap-3">
            <button
              type="button"
              onClick={handleCopyLink}
              className="text-xs text-rose-600 hover:text-rose-700 font-semibold flex items-center gap-1"
            >
              <Copy className="w-3 h-3" />
              <span>Copiar mi enlace</span>
            </button>
            <span className="text-stone-300">•</span>
            <button
              type="button"
              onClick={() => setShowQrModal(true)}
              className="text-xs text-rose-600 hover:text-rose-700 font-semibold flex items-center gap-1"
            >
              <QrCode className="w-3 h-3" />
              <span>Ver Código QR</span>
            </button>
          </div>
        </footer>
      </main>

      {/* LETTER POPUP MODAL */}
      <AnimatePresence>
        {activeLetter && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/35 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 15 }}
              className="bg-amber-50 rounded-3xl border border-amber-200 max-w-lg w-full p-6 sm:p-8 shadow-2xl space-y-5 relative max-h-[85vh] overflow-y-auto"
            >
              <div className="flex items-start justify-between gap-4 border-b border-amber-200/80 pb-4">
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-rose-600 bg-rose-50 py-0.5 px-2.5 rounded-full border border-rose-200">
                    {activeLetter.date}
                  </span>
                  <h3 className="font-['Playfair_Display',serif] text-xl font-bold text-stone-900 mt-1">
                    {activeLetter.title}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveLetter(null)}
                  className="p-1.5 rounded-full text-stone-400 hover:text-stone-700 hover:bg-stone-200/60 transition-colors cursor-pointer"
                >
                  ✕
                </button>
              </div>

              <div className="whitespace-pre-line text-sm sm:text-base text-stone-800 leading-relaxed font-['Caveat',cursive] text-[20px]">
                {activeLetter.content}
              </div>

              <div className="pt-4 border-t border-amber-200/80 flex items-center justify-between">
                <div className="flex items-center gap-1 text-xs text-rose-600 font-semibold">
                  <Heart className="w-3.5 h-3.5 fill-current" />
                  <span>Para {request.partnerName}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveLetter(null)}
                  className="py-1.5 px-4 rounded-xl bg-gradient-to-r from-[#E85D8E] to-[#FF8DAF] hover:from-[#DF4D80] hover:to-[#FF7A9F] text-white font-bold text-xs shadow-md shadow-[#E85D8E]/20 transition-all cursor-pointer"
                >
                  Cerrar
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* QR CODE MODAL */}
      <AnimatePresence>
        {showQrModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/35 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white rounded-3xl border border-stone-200 max-w-sm w-full p-6 sm:p-8 shadow-2xl text-center space-y-5"
            >
              <div className="space-y-1">
                <h3 className="font-['Playfair_Display',serif] text-xl font-bold text-stone-900">
                  Escanea con tu Móvil
                </h3>
                <p className="text-xs text-stone-500">
                  Abre la cámara de cualquier teléfono para abrir directamente este sitio web personalizado.
                </p>
              </div>

              <div className="p-4 rounded-2xl bg-rose-50/50 border border-rose-100 inline-block shadow-inner">
                <img
                  src={qrCodeImageUrl}
                  alt={`QR Code para ${request.applicantName} y ${request.partnerName}`}
                  className="w-56 h-56 mx-auto rounded-xl shadow-2xs"
                  loading="lazy"
                />
              </div>

              <div className="space-y-2">
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className="w-full py-2.5 px-4 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs flex items-center justify-center gap-2 transition-all cursor-pointer shadow-2xs"
                >
                  {copiedLink ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  <span>{copiedLink ? '¡Enlace copiado!' : 'Copiar enlace privado'}</span>
                </button>

                <button
                  type="button"
                  onClick={() => setShowQrModal(false)}
                  className="w-full py-2 px-4 rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-600 font-semibold text-xs transition-colors cursor-pointer"
                >
                  Cerrar
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ADMIN DELETE CONFIRMATION MODAL */}
      <AnimatePresence>
        {showDeleteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/75 backdrop-blur-sm animate-fadeIn">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white border border-slate-200 rounded-3xl p-6 max-w-md w-full text-slate-900 space-y-4 shadow-2xl"
            >
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 bg-rose-50 text-rose-600 border border-rose-100">
                  <Trash2 className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900">
                    ¿Eliminar esta web personalizada?
                  </h3>
                  <p className="text-xs text-slate-500 font-mono mt-0.5">
                    #{request.orderCode} — {request.applicantName} y {request.partnerName}
                  </p>
                </div>
              </div>

              <div className="space-y-2 text-xs text-slate-600 leading-relaxed">
                <p>
                  Esta acción <strong>eliminará de forma permanente</strong> esta página web creada en <em>Mi Web</em>.
                </p>
                <div className="p-3 rounded-2xl bg-rose-50 border border-rose-200/80 text-rose-900 text-[11px] space-y-1">
                  <p className="font-bold">Efectos inmediatos:</p>
                  <ul className="list-disc list-inside space-y-0.5 text-rose-800">
                    <li>El enlace <code className="font-mono bg-rose-100/80 px-1 rounded">/?web={request.orderCode}</code> quedará desactivado.</li>
                    <li>Se borrará irreversiblemente de la base de datos de Firebase Firestore.</li>
                    <li>Regresarás automáticamente a la aplicación principal.</li>
                  </ul>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-2">
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition-all cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={handleConfirmDelete}
                  className="px-5 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold transition-all cursor-pointer shadow-md flex items-center gap-1.5"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>{isDeleting ? 'Eliminando...' : 'Sí, eliminar web'}</span>
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
